import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { TaskTimelineCompletionGroupByEnum } from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import { Values } from '@lib/interfaces/form';
import { FieldNames, FormTypeValue } from 'components/GenerateReportForm/types';
import { setOrReplaceJobInDB } from 'utils/indexedDBUtils';
import getPreparedReportDataFromValues from './getPreparedReportDataFromValues';

type Args = {
  basePeriodEndParam: string;
  basePeriodStartParam: string;
  completionGroupBy: TaskTimelineCompletionGroupByEnum;
  filterParams: Values;
  onCompleted: () => void;
  taskTimelineReportCreateMutation: (v: object) => Promise<unknown>;
  taskIds?: string[];
};

export default function useGenerateReportActions(args: Args) {
  const {
    completionGroupBy,
    filterParams,
    taskIds = [],
    onCompleted,
    taskTimelineReportCreateMutation,
    basePeriodStartParam,
    basePeriodEndParam,
  } = args;
  const { t } = useTranslation();

  const onReportCreateAction = (values: Values) => {
    const formType = _get(values, [FieldNames.FormType, 'value']);
    const params = getPreparedReportDataFromValues({
      completionGroupBy,
      values,
      taskIds,
      filterParams,
      basePeriodStartParam,
      basePeriodEndParam,
    });
    taskTimelineReportCreateMutation({
      variables: { input: { params } },
      onCompleted,
    })
      .then((response) => {
        const jobId = _get(response, 'data.data.jid');
        if (jobId && formType === FormTypeValue.Download) {
          setOrReplaceJobInDB(jobId);
          toast({ content: t('get-tasks-report-download-success') });
        } else if (formType === FormTypeValue.Email) {
          toast({ content: t('get-tasks-report-email-success') });
        }
      })
      .catch((error) => {
        const message = _get(error, 'message');
        let scopeMessage;
        error.graphQLErrors?.forEach((errorItem) => {
          const details = errorItem?.extensions?.details;
          const errorMessageByScope = Array.isArray(details)
            ? details.find(({ path }) => path[0] === 'scope')?.message
            : undefined;
          if (errorMessageByScope) scopeMessage = errorMessageByScope;
        });
        const content = scopeMessage || message || t('something-went-wrong');
        toast({ content });
      });
  };

  return {
    onReportCreateAction,
  };
}
